import React, { useState, useMemo, useRef } from 'react';
import { Select, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';

interface ShipmentMultiSelectProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  currentShipmentId?: string;
  placeholder?: string;
}

interface ShipmentOption {
  id: string;
  reference: string;
  contractRef: string;
  supplier: string;
  buyer: string;
}

export const ShipmentMultiSelect: React.FC<ShipmentMultiSelectProps> = ({
  value = [],
  onChange,
  currentShipmentId,
  placeholder = 'Search and select shipments...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const debounceTimeout = useRef<NodeJS.Timeout>();
  const handleSearch = (val: string) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      setSearchTerm(val);
    }, 300);
  };

  const { data: shipments = [], isLoading } = useQuery({
    queryKey: ['shipments-autocomplete', searchTerm, currentShipmentId],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 2) return [];
      const response = await api.get('/shipments/autocomplete', {
        params: {
          search: searchTerm,
          exclude: currentShipmentId
        }
      }) as { shipments: ShipmentOption[] };
      return response.shipments || [];
    },
    enabled: searchTerm.length >= 2,
    staleTime: 60000,
  });

  const options = useMemo(() => {
    return shipments.map((shipment: ShipmentOption) => ({
      label: (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
          <span style={{ fontWeight: 500 }}>{shipment.reference}</span>
          <span style={{ fontSize: '12px', color: '#888' }}>
            {shipment.supplier} → {shipment.buyer}
          </span>
        </div>
      ),
      value: shipment.reference,
    }));
  }, [shipments]);

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      filterOption={false}
      onSearch={handleSearch}
      notFoundContent={isLoading ? <Spin size="small" /> : null}
      options={options}
      style={{ width: '100%' }}
      showSearch
      allowClear
    />
  );
};
